import { useState, type ReactNode } from "react";
import { format, parseISO } from "date-fns";
import { Clock, X } from "lucide-react";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { Textarea } from "@/modern/components/ui/textarea";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { DatePicker } from "@/modern/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog-shadcn";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modern/components/ui/alert-dialog";
import { cn } from "@/modern/utils";
import type { AttendanceDetail } from "../types";
import { getStatusColor, NOTE_RELATED_ACTION } from "../status/displayStatusColors";

function formatTs(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(parseISO(value), "MMM d · h:mm a");
  } catch {
    return value;
  }
}

function formatWorkDate(value: string | null | undefined): string {
  if (!value) return "";
  try {
    return format(parseISO(value), "EEE, MMM d, yyyy");
  } catch {
    return value;
  }
}

/** API exception_type is a machine code (e.g. NO_TASK); show a readable label. */
const EXCEPTION_TYPE_LABELS: Record<string, string> = {
  NO_TASK: "No Task",
  LATE: "Late",
  EARLY_OUT: "Early Out",
  MISSING_IN: "Missing In",
  MISSING_OUT: "Missing Out",
  OUTSIDE_GEOFENCE: "Outside Geofence",
  OUTSIDE_ACCESS_HOURS: "Outside Access Hours",
  NEEDS_REVIEW: "Needs Review",
};

function formatExceptionType(code: string | null | undefined): string {
  if (!code) return "";
  return (
    EXCEPTION_TYPE_LABELS[code] ??
    code
      .toLowerCase()
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  );
}

function parseMaybe(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  try {
    return parseISO(iso);
  } catch {
    return undefined;
  }
}

function toTimeValue(date: Date | undefined): string {
  if (!date) return "";
  return format(date, "HH:mm");
}

function combineDateAndTime(date: Date | undefined, time: string): Date | undefined {
  if (!date || !time) return undefined;
  const [hours, minutes] = time.split(":");
  const next = new Date(date);
  next.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  return next;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-foreground">{children}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

type AttendanceDetailPanelProps = {
  detail: AttendanceDetail;
  canManage?: boolean;
  isActing?: boolean;
  onClose: () => void;
  onMarkReviewed: (note?: string) => Promise<void>;
  onExcuse: (note?: string) => Promise<void>;
  onAddNote: (note: string) => Promise<void>;
  onCorrect: (body: {
    clock_in_at?: string;
    clock_out_at?: string;
    note?: string;
  }) => Promise<void>;
};

export function AttendanceDetailPanel({
  detail,
  canManage = false,
  isActing,
  onClose,
  onMarkReviewed,
  onExcuse,
  onAddNote,
  onCorrect,
}: AttendanceDetailPanelProps) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [excuseOpen, setExcuseOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [clockInDate, setClockInDate] = useState<Date | undefined>(() => parseMaybe(detail.clock_in_at));
  const [clockInTime, setClockInTime] = useState(() => toTimeValue(parseMaybe(detail.clock_in_at)));
  const [clockOutDate, setClockOutDate] = useState<Date | undefined>(() => parseMaybe(detail.clock_out_at));
  const [clockOutTime, setClockOutTime] = useState(() => toTimeValue(parseMaybe(detail.clock_out_at)));
  const [correctNote, setCorrectNote] = useState("");

  const status = getStatusColor(detail.display_status);
  const displayLabel = detail.display_status_label;
  const workflowLabel =
    detail.status_label &&
    detail.status_label.replace(/[\s_-]+/g, "").toLowerCase() !==
      (displayLabel || "").replace(/[\s_-]+/g, "").toLowerCase()
      ? detail.status_label
      : null;
  const isNoTask = detail.task == null || detail.task_name === "No Task";
  const notes = (detail.notes ?? []).filter((n) => n.related_action === NOTE_RELATED_ACTION.NOTE);
  const hasValidation = (detail.validation_issues?.length ?? 0) > 0;
  const hasHistory = (detail.review_history?.length ?? 0) > 0;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <ScrollArea className="flex-1">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 space-y-2">
            <div>
              <p className="truncate text-base font-semibold leading-tight text-foreground">
                {detail.resource_name}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[detail.resource_role, formatWorkDate(detail.work_date)].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="muted" size="sm" className={cn("gap-1.5", status.bg, status.text)}>
                <span className={cn("h-1.5 w-1.5 rounded-full", status.accent)} />
                {displayLabel}
              </Badge>
              {workflowLabel ? (
                <Badge variant="muted" size="sm">
                  {workflowLabel}
                </Badge>
              ) : null}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-7 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X className="size-3.5" />
          </Button>
        </div>

        <div className="space-y-6 px-5 py-5">
          <Section title="Times">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="grid grid-cols-[1fr_1fr] border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <span>Scheduled</span>
                <span>Actual</span>
              </div>
              <div className="divide-y divide-border text-sm">
                <div className="grid grid-cols-2 px-3 py-2.5">
                  <div>
                    <p className="text-[11px] text-muted-foreground">Start</p>
                    <p className="mt-0.5 tabular-nums">{formatTs(detail.scheduled_start)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">Clock in</p>
                    <p className="mt-0.5 tabular-nums">{formatTs(detail.clock_in_at)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 px-3 py-2.5">
                  <div>
                    <p className="text-[11px] text-muted-foreground">End</p>
                    <p className="mt-0.5 tabular-nums">{formatTs(detail.scheduled_end)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">Clock out</p>
                    <p className="mt-0.5 tabular-nums">{formatTs(detail.clock_out_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Assignment">
            <dl className="space-y-2.5">
              <DetailRow label="Task">{isNoTask ? "No Task" : detail.task_name || "—"}</DetailRow>
              {!isNoTask ? (
                <>
                  <DetailRow label="Client">{detail.project_name || "—"}</DetailRow>
                  <DetailRow label="Location">{detail.location_name || "—"}</DetailRow>
                  <DetailRow label="Geofence">
                    {detail.geofence_radius_m != null ? `${detail.geofence_radius_m}m` : "—"}
                    {detail.clock_in_distance_m != null ? ` · in ${detail.clock_in_distance_m}m` : ""}
                    {detail.clock_out_distance_m != null
                      ? ` · out ${detail.clock_out_distance_m}m`
                      : ""}
                  </DetailRow>
                </>
              ) : null}
              {detail.exception_type ? (
                <DetailRow label="Exception">
                  <span className="font-medium">{formatExceptionType(detail.exception_type)}</span>
                  {detail.status_reason ? (
                    <span className="text-muted-foreground"> — {detail.status_reason}</span>
                  ) : null}
                </DetailRow>
              ) : null}
            </dl>
          </Section>

          {hasValidation ? (
            <Section title="Validation issues">
              <ul className="space-y-1.5 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                {detail.validation_issues.map((issue) => (
                  <li key={issue} className="leading-snug">
                    {issue}
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {notes.length > 0 ? (
            <Section title="Notes">
              <div className="space-y-2">
                {notes.map((n) => (
                  <div
                    key={n.id}
                    className="rounded-lg border border-border bg-muted/20 px-3 py-2.5 text-sm"
                  >
                    <p className="leading-snug text-foreground">{n.body}</p>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      {n.author_name} · {formatTs(n.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}

          {hasHistory ? (
            <Section title="Review history">
              <ol className="space-y-3">
                {detail.review_history.map((h, idx) => (
                  <li key={`${h.created_at}-${idx}`} className="text-sm">
                    <p className="font-medium text-foreground">{h.action_label}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {h.author_name} · {formatTs(h.created_at)}
                    </p>
                    {h.body ? (
                      <p className="mt-1 leading-snug text-muted-foreground">{h.body}</p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </Section>
          ) : null}
        </div>
      </ScrollArea>

      {canManage ? (
        <div className="flex flex-wrap gap-2 border-t border-border bg-background px-5 py-3">
          <Button size="sm" disabled={isActing} onClick={() => void onMarkReviewed()}>
            Mark reviewed
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={isActing}
            onClick={() => setExcuseOpen(true)}
          >
            Excuse
          </Button>
          <Button size="sm" variant="outline" disabled={isActing} onClick={() => setNoteOpen(true)}>
            Add note
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={isActing}
            onClick={() => {
              const inDate = parseMaybe(detail.clock_in_at);
              const outDate = parseMaybe(detail.clock_out_at);
              setClockInDate(inDate);
              setClockInTime(toTimeValue(inDate));
              setClockOutDate(outDate);
              setClockOutTime(toTimeValue(outDate));
              setCorrectOpen(true);
            }}
          >
            Correct times
          </Button>
        </div>
      ) : null}

      <AlertDialog open={excuseOpen} onOpenChange={setExcuseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excuse this attendance?</AlertDialogTitle>
            <AlertDialogDescription>
              Excusing marks the issue as acknowledged and can waive a pending missed-clock
              penalty when incentives are enabled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isActing}
              onClick={(e) => {
                e.preventDefault();
                void (async () => {
                  await onExcuse();
                  setExcuseOpen(false);
                })();
              }}
            >
              Excuse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add note</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reviewer-note" className="text-sm font-medium">
              Note
            </Label>
            <Textarea
              id="reviewer-note"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Reviewer note…"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!noteText.trim() || isActing}
              onClick={async () => {
                await onAddNote(noteText.trim());
                setNoteText("");
                setNoteOpen(false);
              }}
            >
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Correct times</DialogTitle>
            <DialogDescription>
              Update the recorded clock-in and clock-out times for this attendance record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Clock-in date</Label>
                <DatePicker
                  value={clockInDate ?? null}
                  onChange={(date) => setClockInDate(date)}
                  placeholder="Select date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clock-in-time" className="text-sm font-medium">
                  Clock-in time
                </Label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="clock-in-time"
                    type="time"
                    autoComplete="off"
                    value={clockInTime}
                    onChange={(e) => setClockInTime(e.target.value)}
                    className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Clock-out date</Label>
                <DatePicker
                  value={clockOutDate ?? null}
                  onChange={(date) => setClockOutDate(date)}
                  placeholder="Select date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clock-out-time" className="text-sm font-medium">
                  Clock-out time
                </Label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="clock-out-time"
                    type="time"
                    autoComplete="off"
                    value={clockOutTime}
                    onChange={(e) => setClockOutTime(e.target.value)}
                    className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Note (optional)</Label>
              <Textarea
                value={correctNote}
                onChange={(e) => setCorrectNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={isActing}
              onClick={async () => {
                const body: {
                  clock_in_at?: string;
                  clock_out_at?: string;
                  note?: string;
                } = {};
                const inCombined = combineDateAndTime(clockInDate, clockInTime);
                const outCombined = combineDateAndTime(clockOutDate, clockOutTime);
                if (inCombined) body.clock_in_at = inCombined.toISOString();
                if (outCombined) body.clock_out_at = outCombined.toISOString();
                if (correctNote.trim()) body.note = correctNote.trim();
                await onCorrect(body);
                setCorrectOpen(false);
                setCorrectNote("");
              }}
            >
              Save correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
